import { Logger, Presenter, type Scope } from 'wdc-cube'
import type { MercadoVetPresenter } from './mv_presenter'
import { MvWebFilterPanePresenter } from './mv_presenter_filter_pane'
import MvSearchBarPresenter from './mv_presenter_search_bar'
import { buildSummary } from '../the-consumer-market/tcm_filter_summary'
import { WebPageScope, type SortModel } from './mv_scopes'
import { MercadoVetService } from './mv_service'
import type { EstabelecimentoRow, WebBuscaCampo, WebFilter, WebPainelResponse } from './mv_types'
import { errorMessage, normalizeWord, toggle } from './mv_utils'

const LOG = Logger.get('MV-Presenter-Web')

const service = MercadoVetService.singleton()

export default class MvWebPresenter extends Presenter<WebPageScope, MercadoVetPresenter> {
    constructor(owner: MercadoVetPresenter) {
        super(owner, owner.scope.web)

        const s = this.scope
        const children: Scope[] = [
            s.appliedFilters,
            s.totalEstabelecimentos,
            s.totalAbertas24h,
            s.totalFotos,
            s.categorias,
            s.nuvem,
            s.mapa,
            s.detalhamento,
            s.foto
        ]
        for (const child of children) child.update = this.update

        this.filterPane = new MvWebFilterPanePresenter(this, s.filtros)
        this.searchBar = new MvSearchBarPresenter(this, s.busca, {
            placeholder: 'Pesquise por nome ou palavras em destaque',
            modo: 'estabelecimento',
            modos: [
                { value: 'estabelecimento', label: 'Estabelecimento' },
                { value: 'categoria', label: 'Categoria' },
                { value: 'municipio', label: 'Município' }
            ]
        })
    }

    readonly filterPane: MvWebFilterPanePresenter
    readonly searchBar: MvSearchBarPresenter

    get app() {
        return this.owner.app
    }

    // :: State

    private __initialized = false
    private __painelRequest = 0
    private __listagemRequest = 0
    private __categorias: string[] = []
    private __busca: { texto: string; campo: WebBuscaCampo } = { texto: '', campo: 'estabelecimento' }
    private __selecionado?: { id: number; nome: string }

    // :: Lifecycle

    async initialize() {
        const s = this.scope

        this.searchBar.initialize()

        s.totalEstabelecimentos.caption = 'Empresas'
        s.totalEstabelecimentos.captionHint = 'Estabelecimentos veterinários encontrados no Google Maps'
        s.totalAbertas24h.caption = 'Abertas 24h'
        s.totalFotos.caption = 'Fotos'
        s.totalFotos.captionHint = 'Estabelecimentos com foto no Google Maps'

        s.categorias.caption = 'Classificação da Internet'
        s.categorias.onItemClicked = this.action(this.__handleCategoriaClicked)

        s.nuvem.caption = 'Nuvem de Palavras em Destaque'
        s.nuvem.onWordClicked = this.action(this.__handlePalavraClicked)

        s.mapa.caption = 'Geolocalização das Empresas'
        s.mapa.onPointClicked = this.action(this.__handleSelect)

        s.detalhamento.caption = 'Detalhamento das Empresas'
        s.detalhamento.sort = { field: 'uf', desc: false }
        s.detalhamento.onPageChange = this.action(this.__handlePageChange)
        s.detalhamento.onSortChange = this.action(this.__handleSortChange)
        s.detalhamento.onRowClicked = this.action(this.__handleRowClicked)

        s.foto.onFotoErro = this.action(this.__handleFotoErro)

        await this.filterPane.initialize()
    }

    async activate() {
        if (!this.__initialized) {
            this.__initialized = true
            await this.refresh()
        }
    }

    // :: Filtro

    override release() {
        this.searchBar.release()
        this.filterPane.release()
        super.release()
    }

    buildFilter(): WebFilter {
        const filter: WebFilter = this.filterPane.buildWebFilter()
        if (this.__busca.texto) filter.busca = { ...this.__busca }
        if (this.__categorias.length) filter.categoriasClicadas = [...this.__categorias]
        return filter
    }

    onFiltersCleared() {
        this.__categorias = []
        this.__selecionado = undefined
    }

    async fetchData() {
        await this.refresh()
    }

    async onSavedFilterApplied() {
        this.onFiltersCleared()
        await this.refresh()
    }

    async onBuscaChanged(texto: string, campo: string) {
        this.__busca = { texto, campo: (campo as WebBuscaCampo) || 'estabelecimento' }
        await this.refresh()
    }

    // :: Dados

    async refresh() {
        this.filterPane.publish()
        this.__describeAppliedFilters()
        const filter = this.buildFilter()
        await Promise.all([this.__fetchPainel(this.__painelFilter(filter)), this.__fetchListagem(filter, 0, true)])
    }

    private __painelFilter(filter: WebFilter): WebFilter {
        return this.__selecionado ? { ...filter, selecionado: this.__selecionado.id } : filter
    }

    private __describeAppliedFilters() {
        const filters = this.filterPane.describeAppliedFilters()
        const selecao = this.__selecionado && buildSummary('Selecionado na tabela', [this.__selecionado.nome])
        this.scope.appliedFilters.filters = selecao ? [...filters, selecao] : filters
    }

    private async __fetchPainel(filter: WebFilter) {
        const s = this.scope
        const requestId = ++this.__painelRequest
        const loadables = [s.totalEstabelecimentos, s.totalAbertas24h, s.totalFotos, s.categorias, s.nuvem, s.mapa]
        for (const item of loadables) item.loaded = false

        try {
            const resp = await service.fetchWebPainel(filter)
            if (requestId !== this.__painelRequest) return
            s.erro = ''
            this.__applyPainel(resp)
        } catch (caught) {
            if (requestId !== this.__painelRequest) return
            LOG.error('fetchWebPainel', caught)
            s.erro = errorMessage(caught)
        } finally {
            if (requestId === this.__painelRequest) {
                for (const item of loadables) item.loaded = true
            }
        }
    }

    private __applyPainel(resp: WebPainelResponse) {
        const s = this.scope
        s.totalEstabelecimentos.value = resp.total
        s.totalAbertas24h.value = resp.abertas24h
        s.totalFotos.value = resp.fotos

        s.categorias.rows = resp.categorias.map((row) => ({
            id: row.categoria,
            categoria: row.categoria,
            total: row.total,
            selected: this.__categorias.includes(row.categoria)
        }))
        s.categorias.total = resp.categorias.reduce((acc, row) => acc + row.total, 0)

        const selecionadas = this.filterPane.palavrasSelecionadas()
        s.nuvem.words = resp.palavras.map((p) => ({
            texto: p.texto,
            peso: p.peso,
            selected: selecionadas.has(normalizeWord(p.texto))
        }))

        s.mapa.points = resp.mapa
    }

    private async __fetchListagem(filter: WebFilter, page: number, selecionarPrimeiro = false) {
        const table = this.scope.detalhamento
        const requestId = ++this.__listagemRequest
        table.loading = true
        try {
            const resp = await service.fetchWebListagem({ filter, page, pageSize: table.pageSize, sort: table.sort })
            if (requestId !== this.__listagemRequest) return
            table.page = page
            table.total = resp.total
            table.rows = resp.rows

            if (this.__selecionado) return
            const selecionado = resp.rows.find((row) => String(row.id) === table.selectedId)
            if (selecionado) {
                this.__showFoto(selecionado.id)
            } else if (selecionarPrimeiro || !table.selectedId) {
                this.__showFoto(resp.rows[0]?.id)
            }
        } catch (caught) {
            if (requestId !== this.__listagemRequest) return
            LOG.error('fetchWebListagem', caught)
            this.scope.erro = errorMessage(caught)
        } finally {
            if (requestId === this.__listagemRequest) {
                table.loading = false
                table.loaded = true
            }
        }
    }

    private __showFoto(id: number | undefined) {
        const row = this.scope.detalhamento.rows.find((r) => r.id === id) ?? null
        this.__showEstabelecimento(row)
    }

    private __showEstabelecimento(row: EstabelecimentoRow | null) {
        const s = this.scope
        s.detalhamento.selectedId = row ? String(row.id) : ''
        s.mapa.selectedId = s.detalhamento.selectedId
        s.foto.fotoFalhou = false
        s.foto.estabelecimento = row
        s.foto.loaded = true
    }

    // :: Handlers

    private async __handleCategoriaClicked(categoria: string) {
        this.__categorias = toggle(this.__categorias, categoria)
        await this.refresh()
    }

    private async __handlePalavraClicked(texto: string) {
        this.filterPane.togglePalavra(texto)
        await this.refresh()
    }

    private async __handleRowClicked(id: string) {
        const s = this.scope
        const numId = Number(id)
        if (this.__selecionado?.id === numId) {
            this.__selecionado = undefined
            s.detalhamento.selectedId = ''
            s.mapa.selectedId = ''
        } else {
            const row = s.detalhamento.rows.find((r) => r.id === numId)
            if (!row) return
            this.__selecionado = { id: row.id, nome: row.estabelecimento }
            this.__showEstabelecimento(row)
        }
        this.__describeAppliedFilters()
        await this.__fetchPainel(this.__painelFilter(this.buildFilter()))
    }

    private async __handleSelect(id: string) {
        const s = this.scope
        const numId = Number(id)
        if (s.detalhamento.rows.some((r) => r.id === numId)) {
            this.__showFoto(numId)
            return
        }
        try {
            this.__showEstabelecimento(await service.fetchEstabelecimento(numId))
        } catch (caught) {
            LOG.error('fetchEstabelecimento', caught)
        }
    }

    private async __handlePageChange(page: number, pageSize: number) {
        this.scope.detalhamento.pageSize = pageSize
        await this.__fetchListagem(this.buildFilter(), page)
    }

    private async __handleSortChange(sort: SortModel | undefined) {
        this.scope.detalhamento.sort = sort
        await this.__fetchListagem(this.buildFilter(), 0)
    }

    private __handleFotoErro() {
        this.scope.foto.fotoFalhou = true
    }
}
